import { useMemo } from "react";
import { UpcomingMatch } from "../../../types/types";
import {
  getActiveStreamMatch,
  getStreamChannelFromMatch,
  parseTwitchChannelFromUrl,
} from "../../../utils/twitchChannel";

const MATCH_SOON_SECONDS = 3600;
const LIVE_MATCH_WINDOW_MS = 2 * 60 * 60 * 1000;
const MAX_VISIBLE_LIVE_MATCHES = 2;

export interface LiveStreamEntry {
  channel: string;
  link: string;
  matchId: number;
}

export const useHomeStream = (
  upcomingMatches: UpcomingMatch[] | undefined,
  remainingSeconds: number,
  timerLoading = false,
) => {
  const sortedUpcomingMatches = useMemo(
    () =>
      upcomingMatches?.length
        ? [...upcomingMatches].sort(
            (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
          )
        : [],
    [upcomingMatches],
  );

  // remainingSeconds defaults to 0 before the timer resolves — do not treat that as "match soon".
  // After kickoff the timer hits 0; rely on liveMatches for the in-progress window, not "soon".
  const matchSoon = !timerLoading && remainingSeconds > 0 && remainingSeconds <= MATCH_SOON_SECONDS;

  const nextUpcomingMatch = useMemo(() => {
    if (!sortedUpcomingMatches.length) {
      return undefined;
    }

    const now = Date.now();
    return (
      sortedUpcomingMatches.find((match) => new Date(match.time).getTime() > now) ??
      sortedUpcomingMatches[0]
    );
  }, [sortedUpcomingMatches]);

  const liveMatches = useMemo(() => {
    if (timerLoading || !sortedUpcomingMatches.length) {
      return [];
    }

    const now = Date.now();
    return sortedUpcomingMatches
      .filter((match) => {
        const statusIsLive = (match.status ?? "").trim().toUpperCase() === "LIVE";
        const matchTime = new Date(match.time).getTime();
        const isWithinLiveWindow = matchTime <= now && matchTime >= now - LIVE_MATCH_WINDOW_MS;
        return statusIsLive || isWithinLiveWindow;
      })
      .slice(0, MAX_VISIBLE_LIVE_MATCHES);
  }, [sortedUpcomingMatches, timerLoading]);

  const activeStreamMatch = useMemo(
    () => getActiveStreamMatch(upcomingMatches, remainingSeconds),
    [upcomingMatches, remainingSeconds],
  );

  const streamChannel = useMemo(
    () => getStreamChannelFromMatch(activeStreamMatch),
    [activeStreamMatch],
  );

  const liveStreamEntries = useMemo(() => {
    const uniqueByChannel = new Map<string, LiveStreamEntry>();
    liveMatches.forEach((match) => {
      const link = match.link.trim();
      if (!link) return;
      const channel = parseTwitchChannelFromUrl(link);
      if (!channel || uniqueByChannel.has(channel)) return;
      uniqueByChannel.set(channel, { channel, link, matchId: match.id });
    });
    return [...uniqueByChannel.values()];
  }, [liveMatches]);

  return {
    matchSoon,
    nextUpcomingMatch,
    liveMatches,
    liveStreamEntries,
    activeStreamMatch,
    streamChannel,
    streamLink: activeStreamMatch?.link,
  };
};
